import "./App.css";
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Lazy Load Main Views
const SessionView = React.lazy(() => import('@/features/session/components/SessionView').then(module => ({ default: module.SessionView })));
const UserDashboard = React.lazy(() => import('@/features/dashboard/components/UserDashboard').then(module => ({ default: module.UserDashboard })));
const SupportPage = React.lazy(() => import('@/features/support/components/SupportPage').then(module => ({ default: module.SupportPage })));
const UserSettings = React.lazy(() => import('@/features/settings/components/UserSettings').then(module => ({ default: module.UserSettings })));
const LoginScreen = React.lazy(() => import('@/features/auth/components/LoginScreen').then(module => ({ default: module.LoginScreen })));
const JoinSessionModal = React.lazy(() => import('@/features/session/components/JoinSessionModal').then(module => ({ default: module.JoinSessionModal })));

// Critical Views (Static Import for LCP)
import { LandingPage } from '@/features/landing/components/LandingPage';

import { SettingsProvider, useSettings } from '@/shared/contexts/SettingsContext';
import { AuthProvider, useAuth } from '@/shared/contexts/AuthContext';
import { ApiKeyProvider } from '@/shared/contexts/ApiKeyContext';
import { ErrorBoundary, LoadingScreen } from '@/shared/components';
import { AI_PRECONDITION_EVENT } from '@/shared/utils/aiErrors';

const AppContent: React.FC = () => {
  const { t } = useSettings();
  const { currentUser: authUser, loading: authLoading, initialized, initializeAuth, logout } = useAuth();
  const location = useLocation();

  const isPresenterLoggedIn = Boolean(authUser && !authUser.isAnonymous);
  const requiresAuth = location.pathname.startsWith('/dashboard');

  // Join Flow State
  const [showJoinModal, setShowJoinModal] = useState(false);

  // Initialize auth when accessing protected routes
  useEffect(() => {
    if (requiresAuth && !initialized) {
      void initializeAuth();
    }
  }, [requiresAuth, initialized, initializeAuth]);

  useEffect(() => {
    const handleAiPrecondition = () => {
      alert(t('aiTokenPreconditionMessage'));
    };

    window.addEventListener(AI_PRECONDITION_EVENT, handleAiPrecondition);
    return () => window.removeEventListener(AI_PRECONDITION_EVENT, handleAiPrecondition);
  }, [t]);



  // Show loading screen while auth is being initialized for protected routes
  // This prevents premature redirects before auth state is restored from Firebase
  if (requiresAuth && !initialized) {
    return <LoadingScreen />;
  }

  if (authLoading && requiresAuth) {
    return <LoadingScreen />;
  }

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-slate-900 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Skip to content
      </a>
      <main id="main-content">
        <Routes>
          <Route path="/" element={
            <>
              <LandingPage
                onOpenJoinModal={() => setShowJoinModal(true)}
                onLogout={logout}
                user={isPresenterLoggedIn ? authUser : null}
              />

              {/* Join Session Modal */}
              {showJoinModal && (
                <React.Suspense fallback={<LoadingScreen />}>
                  <JoinSessionModal onClose={() => setShowJoinModal(false)} />
                </React.Suspense>
              )}
            </>
          } />
          <Route path="/login" element={
            <React.Suspense fallback={<LoadingScreen />}>
              <LoginScreen />
            </React.Suspense>
          } />
          <Route path="/dashboard" element={
            isPresenterLoggedIn ? (
              <React.Suspense fallback={<LoadingScreen />}>
                <UserDashboard />
              </React.Suspense>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/dashboard/support" element={
            isPresenterLoggedIn ? (
              <React.Suspense fallback={<LoadingScreen />}>
                <SupportPage />
              </React.Suspense>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/dashboard/settings" element={
            isPresenterLoggedIn ? (
              <React.Suspense fallback={<LoadingScreen />}>
                <UserSettings />
              </React.Suspense>
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/session/:sessionId" element={
            <React.Suspense fallback={<LoadingScreen />}>
              <SessionView />
            </React.Suspense>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </>
  );
};

const App: React.FC = () => {
  return (
    <SettingsProvider>
      <AuthProvider>
        <ApiKeyProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </ApiKeyProvider>
      </AuthProvider>
    </SettingsProvider>
  );
};

export default App;
